import type {
  TalentProfile,
  TalentListResponse,
  TalentListParams,
  SkillOption,
  ToolOption,
  ArbiterRole,
  InterviewRequestPayload,
  TalentFacets,
  AiSummaryFeedbackBody,
} from "components/Screens/Marketplace/Customer/types";

import getRequest from "util/APIHelperGet";
import postRequest from "util/APIHelperPost";
import { baseUrl } from "util/APIBaseUrl";
import { getIdToken } from "util/APIHelper";
import deleteRequest from "util/ApiHelperDelete";
import { toApiResult } from "util/apiError";
import type { ApiResult } from "util/apiError";
import type {
  ShortlistResponse,
  InterviewRound,
  CreateInterviewRoundBody,
  PreferencesBody,
} from "components/Screens/Marketplace/Customer/types";
import type { ShortlistFilters } from "components/Screens/Marketplace/Customer/Shortlist/shortlistFilters";

export default async function fetchTalentProfile(talentId: string): Promise<TalentProfile> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/${talentId}/profile/portal`);
  if (!ok) throw new Error(`Failed to fetch talent profile: ${status}`);
  return payload as TalentProfile;
}

/** Downloads a talent's resume PDF with the portal token. */
export async function fetchTalentPdf(talentId: string): Promise<{ blob: Blob; filename: string }> {
  const token = getIdToken();
  const response = await fetch(`${baseUrl}/talents/${encodeURIComponent(talentId)}/profile/portal?pdf=true`, {
    headers: { Authorization: `Bearer ${token.accessToken}` },
  });
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  const blob = await response.blob();
  const match = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/);
  return { blob, filename: match?.[1] || "resumeFile.pdf" };
}

export async function fetchTalentList(params?: TalentListParams): Promise<TalentListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page !== undefined) queryParams.append("page", params.page.toString());
  if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
  if (params?.country) queryParams.append("country", params.country);
  if (params?.language) {
    const val = Array.isArray(params.language) ? params.language.join(",") : params.language;
    if (val) queryParams.append("language", val);
  }
  if (params?.skill) {
    const val = Array.isArray(params.skill) ? params.skill.join(",") : params.skill;
    if (val) queryParams.append("skill", val);
  }
  if (params?.tool) {
    const val = Array.isArray(params.tool) ? params.tool.join(",") : params.tool;
    if (val) queryParams.append("tool", val);
  }
  if (params?.role) {
    const val = Array.isArray(params.role) ? params.role.join(",") : params.role;
    if (val) queryParams.append("role", val);
  }
  if (params?.arbiterRole) {
    const val = Array.isArray(params.arbiterRole) ? params.arbiterRole.join(",") : params.arbiterRole;
    if (val) queryParams.append("arbiterRole", val);
  }
  if (params?.customerId) queryParams.append("customerId", params.customerId);

  const qs = queryParams.toString();
  const url = `${baseUrl}/talents/portal${qs ? `?${qs}` : ""}`;

  const { ok, status, payload } = await getRequest(url);
  if (!ok) throw new Error(`Failed to fetch talents: ${status}`);
  return payload as TalentListResponse;
}

export async function fetchSkills(): Promise<SkillOption[]> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/skills`);
  if (!ok) throw new Error(`Failed to fetch skills: ${status}`);
  return payload as SkillOption[];
}

/** Role and Tools filter options with counts, from what talent actually list (QA PO-4 / PO-7). */
export async function fetchTalentFacets(): Promise<TalentFacets> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/portal/facets`);
  if (!ok) throw new Error(`Failed to fetch filter options: ${status}`);
  return payload as TalentFacets;
}

/** The arbiter roles for the Role filter (title, description, industry). */
export async function fetchRoles(): Promise<ArbiterRole[]> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/roles`);
  if (!ok) throw new Error(`Failed to fetch roles: ${status}`);
  return payload as ArbiterRole[];
}

export async function fetchTools(): Promise<ToolOption[]> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/tools`);
  if (!ok) throw new Error(`Failed to fetch tools: ${status}`);
  return payload as ToolOption[];
}

export async function requestInterview(data: InterviewRequestPayload): Promise<any> {
  const { ok, status, payload } = await postRequest(
    `${baseUrl}/talents/interview-request`,
    JSON.stringify(data),
    false
  );
  if (!ok) throw new Error(`Failed to request interview: ${status}`);
  return payload;
}

export async function submitHireRequest(data: Record<string, any>): Promise<any> {
  const { ok, status, payload } = await postRequest(`${baseUrl}/talents/hire-request`, JSON.stringify(data), false);
  if (!ok) throw new Error(`Failed to submit hire request: ${status}`);
  return payload;
}

export async function fetchHireRequests(customerId: string): Promise<any[]> {
  const { ok, payload } = await getRequest(`${baseUrl}/talents/hire-requests?customerId=${customerId}`);
  if (!ok) return [];
  return (payload as any[]) || [];
}

export async function checkInterviewRequested(customerId: string, talentId: string): Promise<boolean> {
  const { ok, payload } = await getRequest(
    `${baseUrl}/talents/interview-check?customerId=${customerId}&talentId=${talentId}`
  );
  if (!ok) return false;
  return (payload as any)?.requested || false;
}

// PRD-3334 — customerId always comes from the JWT on the portal side; never send it.
export async function fetchShortlist(): Promise<ShortlistResponse> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/shortlist`);
  if (!ok) throw new Error(`Failed to fetch shortlist: ${status}`);
  return payload as ShortlistResponse;
}

/** `filters`: the Hire page filters it was shortlisted under (PRD-3334), when there were any. */
export async function addToShortlist(talentId: string, filters?: ShortlistFilters | null): Promise<ApiResult<unknown>> {
  const response = await postRequest(`${baseUrl}/talents/shortlist`, JSON.stringify({ talentId, ...(filters ? { filters } : {}) }), false);
  return toApiResult(response);
}

export async function removeFromShortlist(talentId: string): Promise<void> {
  const { ok, status } = await deleteRequest(`${baseUrl}/talents/shortlist/${talentId}`);
  if (!ok) throw new Error(`Failed to remove from shortlist: ${status}`);
}

/** Vote on the at-a-glance texts. 409 means a different vote on this version is already stored. */
export async function submitAiSummaryFeedback(talentId: string, body: AiSummaryFeedbackBody): Promise<ApiResult<unknown>> {
  return toApiResult(await postRequest(`${baseUrl}/talents/${talentId}/ai-summary-feedback`, JSON.stringify(body)));
}

export async function fetchInterviewRounds(): Promise<InterviewRound[]> {
  const { ok, status, payload } = await getRequest(`${baseUrl}/talents/interview-rounds`);
  if (!ok) throw new Error(`Failed to fetch interview rounds: ${status}`);
  if (Array.isArray(payload)) return payload as InterviewRound[];
  return ((payload as { rounds?: InterviewRound[] })?.rounds || []) as InterviewRound[];
}

export async function createInterviewRound(body: CreateInterviewRoundBody): Promise<ApiResult<InterviewRound>> {
  const response = await postRequest(`${baseUrl}/talents/interview-rounds`, JSON.stringify(body), false);
  return toApiResult<InterviewRound>(response);
}

export async function postPreferences(body: PreferencesBody): Promise<void> {
  // Preferences are a nice-to-have signal; never block the UI on them.
  await postRequest(`${baseUrl}/talents/preferences`, JSON.stringify(body), false);
}

